import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { User, Mail, Phone, Lock, X, CheckCircle, AlertCircle } from "lucide-react";
import API from "../services/api";

/* ─────────────────────────────────────────
   INLINE STYLES  (no extra CSS file needed)
───────────────────────────────────────── */
const S = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(10, 12, 18, 0.72)",
    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(6px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    padding: "1rem",
    animation: "pm-backdrop-in 0.22s ease forwards",
  },
  modal: {
    background: "#fff",
    borderRadius: "20px",
    width: "100%",
    maxWidth: "440px",
    boxShadow: "0 32px 80px rgba(0,0,0,0.22), 0 2px 8px rgba(0,0,0,0.08)",
    animation: "pm-modal-in 0.28s cubic-bezier(0.34,1.56,0.64,1) forwards",
    overflow: "hidden",
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
  },
  header: {
    padding: "1.5rem 1.5rem 1rem",
    borderBottom: "1px solid #f0f0f0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontWeight: 700,
    fontSize: "1.1rem",
    letterSpacing: "-0.5px",
    flexShrink: 0,
  },
  title: {
    margin: 0,
    fontSize: "1.1rem",
    fontWeight: 700,
    color: "#111",
    letterSpacing: "-0.3px",
  },
  subtitle: {
    margin: "2px 0 0",
    fontSize: "0.78rem",
    color: "#888",
    fontWeight: 400,
  },
  closeBtn: {
    background: "#f5f5f5",
    border: "none",
    borderRadius: "10px",
    width: 36,
    height: 36,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "#666",
    transition: "background 0.15s, color 0.15s",
    flexShrink: 0,
  },
  body: {
    padding: "1.25rem 1.5rem",
    display: "flex",
    flexDirection: "column",
    gap: "0.85rem",
  },
  fieldWrap: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  label: {
    fontSize: "0.72rem",
    fontWeight: 600,
    color: "#555",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  inputRow: {
    display: "flex",
    alignItems: "center",
    border: "1.5px solid #e8e8e8",
    borderRadius: "12px",
    overflow: "hidden",
    background: "#fafafa",
    transition: "border-color 0.15s, background 0.15s",
  },
  inputRowErr: {
    borderColor: "#f87171",
    background: "#fff8f8",
  },
  inputIcon: {
    padding: "0 0 0 0.9rem",
    display: "flex",
    alignItems: "center",
    color: "#aaa",
    flexShrink: 0,
  },
  input: {
    flex: 1,
    border: "none",
    background: "transparent",
    padding: "0.72rem 0.9rem",
    fontSize: "0.9rem",
    color: "#111",
    outline: "none",
    fontFamily: "inherit",
    width: "100%",
  },
  errText: {
    fontSize: "0.75rem",
    color: "#ef4444",
    margin: 0,
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  footer: {
    padding: "0 1.5rem 1.5rem",
    display: "flex",
    gap: "0.75rem",
  },
  cancelBtn: {
    flex: 1,
    padding: "0.75rem",
    border: "1.5px solid #e8e8e8",
    borderRadius: "12px",
    background: "#fff",
    fontSize: "0.9rem",
    fontWeight: 600,
    color: "#555",
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "background 0.15s",
  },
  updateBtn: {
    flex: 2,
    padding: "0.75rem",
    border: "none",
    borderRadius: "12px",
    background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
    fontSize: "0.9rem",
    fontWeight: 700,
    color: "#fff",
    cursor: "pointer",
    fontFamily: "inherit",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    transition: "opacity 0.15s, transform 0.1s",
    letterSpacing: "-0.2px",
  },
  updateBtnDisabled: {
    opacity: 0.65,
    cursor: "not-allowed",
  },
  spinner: {
    width: 16,
    height: 16,
    border: "2.5px solid rgba(255,255,255,0.4)",
    borderTop: "2.5px solid #fff",
    borderRadius: "50%",
    animation: "pm-spin 0.7s linear infinite",
    flexShrink: 0,
  },
  /* TOAST */
  toastWrap: {
    position: "fixed",
    bottom: "1.5rem",
    right: "1.5rem",
    zIndex: 99999,
    animation: "pm-toast-in 0.3s cubic-bezier(0.34,1.56,0.64,1) forwards",
  },
  toast: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "0.85rem 1.1rem",
    borderRadius: "14px",
    background: "#111",
    color: "#fff",
    fontSize: "0.88rem",
    fontWeight: 500,
    boxShadow: "0 8px 32px rgba(0,0,0,0.28)",
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    maxWidth: 320,
  },
  toastSuccess: { background: "#16a34a" },
  toastError: { background: "#dc2626" },
};

/* ─────────────────────────────────────────
   KEYFRAMES injected once
───────────────────────────────────────── */
const KEYFRAMES = `
  @keyframes pm-backdrop-in { from { opacity:0 } to { opacity:1 } }
  @keyframes pm-modal-in    { from { opacity:0; transform:scale(0.88) translateY(12px) } to { opacity:1; transform:scale(1) translateY(0) } }
  @keyframes pm-spin        { to   { transform:rotate(360deg) } }
  @keyframes pm-toast-in    { from { opacity:0; transform:translateY(14px) } to { opacity:1; transform:translateY(0) } }
`;

function injectKeyframes() {
  if (document.getElementById("pm-keyframes")) return;
  const tag = document.createElement("style");
  tag.id = "pm-keyframes";
  tag.textContent = KEYFRAMES;
  document.head.appendChild(tag);
}

/* ─────────────────────────────────────────
   TOAST COMPONENT
───────────────────────────────────────── */
function Toast({ message, type = "success", onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 3200);
    return () => clearTimeout(t);
  }, [onDone]);

  return createPortal(
    <div style={S.toastWrap}>
      <div style={{ ...S.toast, ...(type === "success" ? S.toastSuccess : S.toastError) }}>
        {type === "success"
          ? <CheckCircle size={17} />
          : <AlertCircle size={17} />}
        {message}
      </div>
    </div>,
    document.body
  );
}

/* ─────────────────────────────────────────
   FIELD COMPONENT
───────────────────────────────────────── */
function Field({ label, icon: Icon, type = "text", name, value, placeholder, onChange, error }) {
  return (
    <div style={S.fieldWrap}>
      <span style={S.label}>{label}</span>
      <div style={{ ...S.inputRow, ...(error ? S.inputRowErr : {}) }}>
        <span style={S.inputIcon}><Icon size={15} /></span>
        <input
          type={type}
          name={name}
          value={value}
          placeholder={placeholder}
          onChange={onChange}
          style={S.input}
          autoComplete={type === "password" ? "new-password" : "off"}
        />
      </div>
      {error && (
        <p style={S.errText}>
          <AlertCircle size={12} /> {error}
        </p>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────
   MAIN MODAL
───────────────────────────────────────── */
function ProfileModal() {
  const [show, setShow]       = useState(false);
  const [form, setForm]       = useState({ name: "", email: "", phone: "", password: "" });
  const [errors, setErrors]   = useState({});
  const [loading, setLoading] = useState(false);
  const [toast, setToast]     = useState(null); // { message, type }

  // inject keyframes once
  useEffect(() => { injectKeyframes(); }, []);

  // listen for open event
  useEffect(() => {
    const openModal = () => {
      setShow(true);
      setErrors({});

      API.get("/profile")
        .then((res) => {
          if (res.data.success) {
            const u = res.data.data;
            setForm({ name: u.name || "", email: u.email || "", phone: u.phone || "", password: "" });
          }
        })
        .catch((err) => console.error(err));
    };

    window.addEventListener("openProfileModal", openModal);
    return () => window.removeEventListener("openProfileModal", openModal);
  }, []);

  // close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setShow(false); };
    if (show) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [show]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim())  errs.name  = "Name is required";
    if (!form.email.trim()) {
      errs.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errs.email = "Invalid email format";
    }
    if (!form.phone.trim()) errs.phone = "Phone is required";
    if (form.password && form.password.length < 6) errs.password = "Minimum 6 characters";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleUpdate = () => {
    if (!validate()) return;
    setLoading(true);

    API.post("/profile/update", form)
      .then((res) => {
        if (res.data.success) {
          localStorage.setItem("user", JSON.stringify(res.data.data));
          window.dispatchEvent(new Event("storageUpdate"));
          setShow(false);
          setToast({ message: "Profile updated successfully!", type: "success" });
        }
      })
      .catch(() => {
        setToast({ message: "Update failed. Try again.", type: "error" });
      })
      .finally(() => setLoading(false));
  };

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const initials = form.name ? form.name.charAt(0).toUpperCase() : (user?.name?.charAt(0)?.toUpperCase() || "U");

  return (
    <>
      {/* TOAST */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onDone={() => setToast(null)}
        />
      )}

      {/* MODAL */}
      {show && createPortal(
        <div style={S.backdrop} onClick={(e) => { if (e.target === e.currentTarget) setShow(false); }}>
          <div style={S.modal} role="dialog" aria-modal="true" aria-labelledby="pm-title">

            {/* HEADER */}
            <div style={S.header}>
              <div style={S.headerLeft}>
                <div style={S.avatar}>{initials}</div>
                <div>
                  <h2 id="pm-title" style={S.title}>Profile Management</h2>
                  <p style={S.subtitle}>Update your account details</p>
                </div>
              </div>
              <button
                style={S.closeBtn}
                onClick={() => setShow(false)}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#ebebeb"; e.currentTarget.style.color = "#111"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "#f5f5f5"; e.currentTarget.style.color = "#666"; }}
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>

            {/* BODY */}
            <div style={S.body}>
              <Field label="Full Name"   icon={User}  name="name"     value={form.name}     placeholder="Your name"               onChange={handleChange} error={errors.name} />
              <Field label="Email"       icon={Mail}  type="email" name="email" value={form.email} placeholder="your@email.com"   onChange={handleChange} error={errors.email} />
              <Field label="Phone"       icon={Phone} name="phone"    value={form.phone}    placeholder="+91 98765 43210"         onChange={handleChange} error={errors.phone} />
              <Field label="New Password (optional)" icon={Lock} type="password" name="password" value={form.password} placeholder="Leave blank to keep current" onChange={handleChange} error={errors.password} />
            </div>

            {/* FOOTER */}
            <div style={S.footer}>
              <button
                style={S.cancelBtn}
                onClick={() => setShow(false)}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#f5f5f5"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}
              >
                Cancel
              </button>

              <button
                style={{ ...S.updateBtn, ...(loading ? S.updateBtnDisabled : {}) }}
                onClick={handleUpdate}
                disabled={loading}
                onMouseEnter={(e) => { if (!loading) e.currentTarget.style.opacity = "0.88"; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
              >
                {loading ? (
                  <><div style={S.spinner} /> Updating...</>
                ) : (
                  <><CheckCircle size={16} /> Save Changes</>
                )}
              </button>
            </div>

          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export default ProfileModal;